<template>
  <model-form
    ref="modelForm"
    :model="showingBadge"
    modal-type="modal"
  >
    <div class="column">
      <div class="row q-gutter-md">
        <model-form-input
          field="displayName"
          class="col"
          label="Display Name"
          :rules="[val => !!val || 'Field is required']"
          autofocus
        />
        <model-form-select
          field="type"
          class="col"
          label="Type"
          :options="showingBadge.fieldOptions('type')"
          emit-value
          map-options
          :rules="[val => val !== null || 'Field is required']"
        />
        <template v-if="showingBadge.type === 'icon' || showingBadge.type === 'name-and-icon'">
          <q-img
            v-if="showingBadge.iconImage"
            :src="showingBadge.iconImagePreviewUrl"
            class="col-auto q-mb-lg"
            style="height: 50px; width: 50px;"
            @click="$refs.iconPickerDialog.show()"
          />
          <q-btn
            v-else
            :icon="showingBadge.iconName"
            size="lx"
            class="col-auto q-mb-lg"
            outline
            :disable="showingBadge.isTranslation"
            @click="$refs.iconPickerDialog.show()"
          />
        </template>
      </div>

      <div class="row q-gutter-md">
        <model-form-input
          field="title"
          class="col"
          label="Title"
          :rules="[val => !!val || 'Field is required']"
        />
      </div>
      <div class="row q-col-gutter-sm">
        <model-form-input
          field="description"
          class="col-12"
          type="textarea"
          label="Description"
        />
      </div>
      <div class="row q-gutter-sm">
        <model-form-checkbox
          field="conferPriority"
          class="col-auto"
          label="Confer Priority"
        />
        <q-icon
          class="col-auto q-mt-md"
          name="info"
          size="sm"
        >
          <q-tooltip>
            Showings with priority badges will appear first on your website.<br><br>
            If there are multiple priority showings, the display order of any showing badges determines the order of the showings.<br><br>
            Movies with priority showings will also be prioritized.
          </q-tooltip>
        </q-icon>
      </div>
      <q-tabs
        v-model="tab"
        dense
        class="text-responsive"
        active-color="primary"
        indicator-color="primary"
        align="justify"
      >
        <q-tab
          name="tms"
          label="TMS"
        />
        <q-tab
          name="signage"
          label="Signage"
        />
        <q-tab
          name="button"
          label="Button"
        />
      </q-tabs>
      <q-separator />
      <q-tab-panels
        v-model="tab"
        animated
      >
        <q-tab-panel name="tms">
          <div class="row q-col-gutter-sm items-start q-mt-md">
            <q-toggle
              :model-value="showingBadge.tmsTag !== null"
              class="col-6"
              label="Has Associated TMS Tag"
              bottom-slots
              :disable="showingBadge.isTranslation"
              @update:model-value="(value) => value ? (showingBadge.tmsTag = '') : (showingBadge.tmsTag = null) "
            />
            <q-input
              v-if="showingBadge.tmsTag !== null"
              :model-value="showingBadge.tmsTag"
              class="col"
              label="TMS Tag"
              :rules="[val => val === null || val.length > 0 || 'Field is required']"
              bottom-slots
              :disable="showingBadge.isTranslation"
              @update:model-value="val => { showingBadge.tmsTag = val.trim() }"
            />
          </div>
          <div class="row q-col-gutter-sm items-start q-mt-md">
            <q-toggle
              :model-value="showingBadge.tmsTemplate !== null"
              class="col-6"
              label="Has Associated TMS Template"
              bottom-slots
              :disable="showingBadge.isTranslation"
              @update:model-value="(value) => value ? (showingBadge.tmsTemplate = '') : (showingBadge.tmsTemplate = null) "
            />
            <q-input
              v-if="showingBadge.tmsTemplate !== null"
              :model-value="showingBadge.tmsTemplate"
              class="col"
              label="TMS Template Name"
              :rules="[val => val === null || val.length > 0 || 'Field is required']"
              bottom-slots
              :disable="showingBadge.isTranslation"
              @update:model-value="val => { showingBadge.tmsTemplate = val.trim() }"
            />
          </div>
        </q-tab-panel>
        <q-tab-panel name="signage">
          <div class="row q-col-gutter-sm items-start q-mt-md">
            <model-form-toggle
              field="displayOnSignage"
              class="col-6"
              label="Display On Signage"
              bottom-slots
            >
              <q-tooltip>
                When selected, this showing badge will be displayed on signage (session signs only).
              </q-tooltip>
            </model-form-toggle>
          </div>
        </q-tab-panel>
        <q-tab-panel name="button">
          <div class="row q-col-gutter-sm items-start q-mt-md">
            <model-form-toggle
              field="isButtonOnly"
              class="col-6"
              label="Restrict to Button"
              bottom-slots
            >
              <q-tooltip>
                When selected, this showing badge will only appear inside or below showing buttons. It will not be used as a common showing badge.
              </q-tooltip>
            </model-form-toggle>
            <q-input
              v-model="showingBadge.color"
              label="Display Button Color"
              class="col-6"
              :bg-color="showingBadge.color"
            >
              <template #append>
                <q-icon
                  name="colorize"
                  class="cursor-pointer"
                >
                  <q-popup-proxy
                    cover
                    transition-show="scale"
                    transition-hide="scale"
                  >
                    <q-color v-model="showingBadge.color" />
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>
          <div class="row q-col-gutter-sm items-start q-mt-md">
            <model-form-toggle
              field="displayTitleOnButton"
              class="col-12"
              label="Display Title on Button Instead"
              bottom-slots
            >
              <q-tooltip>
                When selected, this showing badge will only display title with button.
              </q-tooltip>
            </model-form-toggle>
          </div>
        </q-tab-panel>
      </q-tab-panels>

      <div class="text-h5 q-mt-lg q-mb-sm">
        Preview
      </div>
      <div class="row">
        <showing-badge
          :showing-badge="showingBadge"
          outline
          color="dark"
        />
        <div />
      </div>
    </div>
    <icon-picker-dialog
      ref="iconPickerDialog"
      :model="showingBadge"
      icon-property="iconName"
      icon-image="iconImage"
      icon-image-preview-url="iconImagePreviewUrl"
    />
  </model-form>
</template>
<script>
import IconPickerDialog from 'src/modules/admin/components/IconPickerDialog.vue'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import ShowingBadge from 'src/modules/admin/showing-badge/components/ShowingBadge'

export default {
  name: 'ShowingBadgeForm',
  components: {
    ModelForm,
    ShowingBadge,
    IconPickerDialog
  },
  data () {
    return {
      tab: 'tms'
    }
  },
  models: {
    showingBadge: 'default'
  }
}

</script>
